import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { getStatsSharingTokenInfos } from 'actions/consent';
import { changeLocale } from 'actions/env';
import { useDispatch } from 'utils/redux';

import {
  dataSafeIcon,
  logo,
  mediaKitEn,
  mediaKitFr,
  shareStatsEn,
  shareStatsFr,
  step1En,
  step1Fr,
  step2En,
  step2Fr,
  step3En,
  step3Fr,
  step4En,
  step4Fr,
} from 'assets/images/kreators';
import LinkInstagramButton from 'components/atoms/Buttons/LinkInstagramButton';
import Markdown from 'components/atoms/Markdown';
import Typography from 'components/atoms/Typography';

import styles from './KolLandingPage.module.scss';

const browserLang = navigator.language || navigator.userLanguage;

const howToLinks = {
  en: {
    link: `https://static.kolsquare.com/assets/kol_landing_page/${encodeURIComponent('For KOLs - VE.pdf')}`,
    filename: 'For KOLs - VE.pdf',
  },
  fr: {
    link: `https://static.kolsquare.com/assets/kol_landing_page/${encodeURIComponent('For KOLs - VF.pdf')}`,
    filename: 'For KOLs - VF.pdf',
  },
};

const KolLandingPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { token } = useParams<{ token: string }>();

  const [companyName, setCompanyName] = useState('');

  const locale = browserLang?.split('-')[0] !== 'fr' ? 'en' : 'fr';

  useEffect(() => {
    dispatch(changeLocale(locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (token) {
      dispatch(getStatsSharingTokenInfos(token)).then((response) => {
        setCompanyName(response.salesforce_account_name);
      });
    }
  }, [dispatch, token]);

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        {/*  eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <a href="#advantages" target="_self" rel="noopener noreferrer">
          <img src={logo} alt="logo.svg" />
        </a>
        <div className={styles.menu}>
          <Typography variant="label/large" className={styles.descriptionText}>
            <a href="#advantages">
              <FormattedMessage id="KolLandingPage.menu.advantages" defaultMessage="Benefits" />
            </a>
          </Typography>
          <Typography variant="label/large" className={styles.descriptionText}>
            <a href="#howToLinkInstagram">
              <FormattedMessage id="KolLandingPage.menu.howToDoIt" defaultMessage="How does it work?" />
            </a>
          </Typography>
          <Typography variant="label/large" className={styles.descriptionText}>
            <a href={`https://www.kolsquare.com/${locale}`} target="_blank" rel="noreferrer">
              <FormattedMessage id="KolLandingPage.menu.whoIsKolsquare" defaultMessage="About Kolsquare" />
            </a>
          </Typography>
        </div>
      </div>
      <div className={styles.innerContainer}>
        <div className={styles.paddedTile}>
          <div className="flex gap42 aic jcsb">
            <div className="flex fdc gap32 f1 padTop40">
              <div className="flex fdc gap22 aifs">
                <div className={styles.purpleBgTitle}>
                  <FormattedMessage id="KolLandingPage.part1.title" defaultMessage="Instagram connection" />
                </div>
                <div className={styles.bigTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part1.companyWantsAccess"
                    defaultMessage="{companyName} wants to access your Instagram statistics."
                    values={{ companyName }}
                  />
                </div>
                <Typography variant="text/body medium" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part1.companyWantsAccess.message"
                    defaultMessage="{companyName} wants to learn more about your content. Log in to your statistics via Instaconnect to share them with brands."
                    values={{ companyName }}
                  />
                </Typography>
              </div>
              <div className={styles.topButtonContainer}>
                <LinkInstagramButton
                  from="kol-landing-page"
                  showTermsAndConditionsInsideModal
                  companyName={companyName}
                />
                <a
                  type="download"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-nowrap"
                  href={howToLinks[locale].link}
                  download={howToLinks[locale].filename}
                >
                  <button className={styles.ctaSecondary} type="button">
                    <FormattedMessage id="KolLandingPage.part1.cta.howTo" defaultMessage="How to" />
                  </button>
                </a>
              </div>
            </div>

            <img
              srcSet="https://static.kolsquare.com/assets/kol_landing_page/part-1.png 2x"
              alt="part-1-no-brands.png"
              className={styles.kolImage}
            />
          </div>
        </div>
        <div className={styles.paddedTile}>
          <div className={styles.centeredTitleTile}>
            <div className={`${styles.mediumTitle} tac`} id="advantages">
              <FormattedMessage
                id="KolLandingPage.part2.whyLinkMyInstagram"
                defaultMessage="Why Link My Instagram Account to Kolsquare?"
              />
            </div>
            <Typography variant="label/large" className={`${styles.descriptionText} tac`}>
              <FormattedMessage
                id="KolLandingPage.part2.message"
                defaultMessage="By connecting your Instagram account, you increase your chances of being noticed by leading brands such as:"
              />
            </Typography>
          </div>
        </div>
        {/* Part 6: Moved up */}
        <div className={styles.paddedTile}>
          <div className={`${styles.bgPurple} ${styles.dataSafeCard}`}>
            <div className={styles.iconContainer}>
              <img src={dataSafeIcon} alt="Data safe icon" />
            </div>
            <div className={styles.contentContainer}>
              <p className={styles.dataSafeTitle}>
                <FormattedMessage id="KolLandingPage.part6.yourDataIsSafe" defaultMessage="Your Data Is Safe" />
              </p>
              <p className={styles.dataSafeText}>
                <FormattedMessage
                  id="KolLandingPage.part6.yourDataIsSafe.message"
                  defaultMessage="We only collect your Instagram performance data — like stats from Reels, posts, and stories — to share with verified brands for potential collaborations. This process is 100% compliant with Meta's rules and data policies. Brands will see that your account is connected, helping you stand out and boost your chances of being selected."
                />
              </p>
            </div>
          </div>
        </div>
        <div className={styles.paddedTile}>
          <div className={styles.threeHorizontalTile}>
            {/* 1/3 */}
            <div className={styles.bgLightGrey}>
              <div className="flex fdc gap22 ais">
                <div />
                <img
                  srcSet="https://static.kolsquare.com/assets/kol_landing_page/part-1-brands-1-new.png 1.5x"
                  alt="part-1-brands-1-new.png"
                  className={styles.fullWidthImage}
                />
                <img
                  srcSet="https://static.kolsquare.com/assets/kol_landing_page/part-1-brands-2-new.png 1.5x"
                  alt="part-1-brands-2-new.png"
                  className={styles.fullWidthImage}
                />
                <div />
              </div>
              <div className={styles.tileTextBlock}>
                <p className={styles.tileTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part3.workWithBestBrands"
                    defaultMessage="Work with the best brands."
                  />
                </p>
                <p className={styles.tileDescription}>
                  <FormattedMessage
                    id="KolLandingPage.part3.workWithBestBrands.message"
                    defaultMessage="Top brands rely on Kolsquare to find and collaborate with the right creators. By linking your Instagram account, you make your real stats visible—boosting your chances of being selected for high-quality campaigns."
                  />
                </p>
              </div>
            </div>
            {/* 2/3 */}
            <div className={styles.bgLightGrey}>
              <img
                srcSet={locale === 'fr' ? shareStatsFr : shareStatsEn}
                alt="share-stats.svg"
                className={styles.fullWidthImage}
              />
              <div className={styles.tileTextBlock}>
                <p className={styles.tileTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part3.facilitateYourExchanges"
                    defaultMessage="Facilitate your exchanges with your clients."
                  />
                </p>
                <p className={styles.tileDescription}>
                  <FormattedMessage
                    id="KolLandingPage.part3.facilitateYourExchanges.message"
                    defaultMessage="Skip the screenshots and back-and-forth. With your account linked, brands can view your performance instantly, making communication quick and easy."
                  />
                </p>
              </div>
            </div>
            {/* 3/3 */}
            <div className={styles.bgLightGrey}>
              <div>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-3-${locale}.png 1.5x`}
                  alt="part-1-brands-1.png"
                  className={styles.fixedRightImage}
                />
              </div>
              <div className={styles.tileTextBlock}>
                <p className={styles.tileTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part3.increaseYourRevenue"
                    defaultMessage="Increase your revenue."
                  />
                </p>
                <p className={styles.tileDescription}>
                  <FormattedMessage
                    id="KolLandingPage.part3.increaseYourRevenue.message"
                    defaultMessage="Linked stats make it easier for brands to see your value—so you can land better deals, secure more collaborations, and grow your income faster."
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Personalized Media Kit Section */}
        <div className={`${styles.bgLightGrey} ${styles.mediaKitSection}`}>
          <div className={styles.mediaKitTextBlock}>
            <p className={styles.mediaKitTitle}>
              <FormattedMessage
                id="KolLandingPage.mediaKitSection.title"
                defaultMessage="A personalized 100% free media kit to make a difference"
              />
            </p>
            <p className={styles.descriptionText}>
              <FormattedMessage
                id="KolLandingPage.mediaKitSection.description"
                defaultMessage="Create a free media kit that works beyond Kolsquare. Connect your Instagram stats, pick the bio and metrics to share, then send the link to any brand or agency."
              />
            </p>
          </div>
          <div className={styles.mediaKitImageContainer}>
            <img
              src={locale === 'fr' ? mediaKitFr : mediaKitEn}
              alt="Media kit preview"
              className={styles.mediaKitImage}
            />
          </div>
        </div>
        {/* Part 4 */}
        <div className={styles.bigBlueContainer}>
          <img
            srcSet="https://static.kolsquare.com/assets/kol_landing_page/instagram-banner-new.png 1.5x"
            alt="instagram-banner.png"
            className={styles.instagramBanner}
          />
          {/* Title */}
          <div className={styles.centeredTitleTile}>
            <p className={styles.purpleSubTitle}>
              <FormattedMessage
                id="KolLandingPage.part4.quickSetup"
                defaultMessage="Quick setup in four simple steps"
              />
            </p>
            <p className={styles.sectionMainTitle} id="howToLinkInstagram">
              <FormattedMessage
                id="KolLandingPage.part4.howToLinkInstagram"
                defaultMessage="How to Link My Instagram Account?"
              />
            </p>
            <p className={styles.sectionDescription}>
              <FormattedMessage
                id="KolLandingPage.part4.howToLinkInstagram.message"
                defaultMessage="Before starting, make sure that: Your Instagram account is a business account, Your Instagram account is linked to a Facebook Page, You are an admin of that Facebook Page."
              />
            </p>
          </div>
          <div className={styles.columnContainer}>
            {/* Step 1 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img srcSet={locale === 'fr' ? step1Fr : step1En} alt="link-1.svg" />
              </div>
              <div className={styles.stepContainer}>
                <div className={styles.stepTextGroup}>
                  <div className={styles.stepHeader}>
                    <div className={styles.stepLabel}>
                      <FormattedMessage id="KolLandingPage.part4.step1" defaultMessage="Step 1" />
                    </div>
                    <div className={styles.stepTitle}>
                      <FormattedMessage
                        id="KolLandingPage.part4.step1.title"
                        defaultMessage="Login to your Facebook account"
                      />
                    </div>
                  </div>
                  <p className={styles.stepDescription}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step1.message"
                      defaultMessage="Use the credentials linked to your Instagram profile."
                    />
                  </p>
                </div>
                <LinkInstagramButton
                  from="kol-landing-page"
                  showTermsAndConditionsInsideModal
                  companyName={companyName}
                />
              </div>
            </div>
            {/* Step 2 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img srcSet={locale === 'fr' ? step2Fr : step2En} alt="part-4-2.png" />
              </div>
              <div className={styles.stepContainer}>
                <div className={styles.stepTextGroup}>
                  <div className={styles.stepHeader}>
                    <div className={styles.stepLabel}>
                      <FormattedMessage id="KolLandingPage.part4.step2" defaultMessage="Step 2" />
                    </div>
                    <div className={styles.stepTitle}>
                      <FormattedMessage
                        id="KolLandingPage.part4.step2.title"
                        defaultMessage="Login or reconnect your Facebook account to Kreators (by Kolsquare) account"
                      />
                    </div>
                  </div>
                  <p className={styles.stepDescription}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step2.message"
                      defaultMessage='Click "Edit previous settings" to make sure everything is properly synced.'
                    />
                  </p>
                </div>
              </div>
            </div>
            {/* Step 3 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img srcSet={locale === 'fr' ? step3Fr : step3En} alt="part-4-3.png" />
              </div>
              <div className={styles.stepContainer}>
                <div className={styles.stepTextGroup}>
                  <div className={styles.stepHeader}>
                    <div className={styles.stepLabel}>
                      <FormattedMessage id="KolLandingPage.part4.step3" defaultMessage="Step 3" />
                    </div>
                    <div className={styles.stepTitle}>
                      <FormattedMessage id="KolLandingPage.part4.step3.title" defaultMessage="Select your accounts" />
                    </div>
                  </div>
                  <ul className={styles.stepList}>
                    <li>
                      <FormattedMessage
                        id="KolLandingPage.part4.step3.message1"
                        defaultMessage="Choose the business account linked to your Facebook page - it must be active and visible in Meta Business Suite."
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id="KolLandingPage.part4.step3.message2"
                        defaultMessage="Pick one Facebook page only - this is the one that will be connected."
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id="KolLandingPage.part4.step3.message3"
                        defaultMessage="Choose the Instagram account linked to the selected Facebook page. Make sure that there is only one Instagram account linked per page."
                      />
                    </li>
                  </ul>
                </div>
                <a
                  href="https://www.facebook.com/help/2978174759158711/?helpref=uf_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.stepLink}
                >
                  <FormattedMessage
                    id="KolLandingPage.part4.step3.link"
                    defaultMessage="Don't have a business account?"
                  />
                </a>
              </div>
            </div>
            {/* Step 4 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img srcSet={locale === 'fr' ? step4Fr : step4En} alt="part-4-4.png" />
              </div>
              <div className={styles.stepContainer}>
                <div className={styles.stepTextGroup}>
                  <div className={styles.stepHeader}>
                    <div className={styles.stepLabel}>
                      <FormattedMessage id="KolLandingPage.part4.step4" defaultMessage="Step 4" />
                    </div>
                    <div className={styles.stepTitle}>
                      <FormattedMessage
                        id="KolLandingPage.part4.step4.title"
                        defaultMessage="Final Check: Authorizations"
                      />
                    </div>
                  </div>
                  <p className={styles.stepDescription}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step4.message"
                      defaultMessage="Ensure that all required permissions are granted to Kreators (by Kolsquare) and Meta. Double-check that Kreators (by Kolsquare) has full access to your selected business assets."
                    />
                  </p>
                </div>
                <div className={styles.helpCard}>
                  <div className={styles.helpCardTitle}>
                    {intl.formatMessage({ id: 'KolLandingPage.part4.step4.needHelp', defaultMessage: 'Need help?' })}
                  </div>
                  <Markdown className={styles.helpCardText}>
                    {intl.formatMessage(
                      {
                        id: 'KolLandingPage.part4.step4.needHelpText',
                        defaultMessage:
                          "Download our How To Guide or reach out to us at support@kolsquare.com — we're here for you!",
                      },
                      {
                        link: howToLinks[locale].link,
                      },
                    )}
                  </Markdown>
                </div>
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <LinkInstagramButton
                from="kol-landing-page"
                showTermsAndConditionsInsideModal
                companyName={companyName}
              />
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerLogo}>
            <img src={logo} alt="Kreators Logo" />
          </div>
          <div className={styles.footerMenu}>
            <span>Kreators</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KolLandingPage;
